import { useState, useEffect, useRef, useCallback } from 'react'
import styles from './styles.module.scss'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaChevronDown, FaFileCsv, FaUpload } from 'react-icons/fa'
import { useToast } from '../../ui/toast'
import api from '../../../helpers/apiRequester'
import { getLoginUrl } from '../../../helpers/authConfig'

interface IntroducerOption {
    value: string
    label: string
}

interface CsvProgressEntry {
    type: string
    stage: string
    percent: number
    message: string
    data?: any
    timestamp: Date
}

interface CsvResult {
    batchGroup: string
    notes: string | null
    requestedEmails: number
    matchedClients: number
    alreadyInFundingMonitorCount: number
    alreadyInFundingMonitorSample?: string[]
    notFoundEmailsCount: number
    notFoundEmailsSample?: string[]
    insertResults?: {
        totalInserted: number
        totalFailed: number
    }
}

const CSV_DEFAULT_INTRODUCERS = [
    'FunderAdditional',
    'FundingExtended',
    'PCPMO26',
    'Mail Referral',
    'ADL',
    'PCP Sentinel',
]

export const CollectFundingClientsView = () => {
    const [commissionPercentage, setCommissionPercentage] = useState<string>('')
    const [statutoryInterestPercentage, setStatutoryInterestPercentage] = useState<string>('')
    const [minimumClientValue, setMinimumClientValue] = useState<string>('')
    const [calculationDate, setCalculationDate] = useState<string>('')
    const [customDate, setCustomDate] = useState<string>('')
    const [selectedIntroducers, setSelectedIntroducers] = useState<string[]>([])
    const [maximumClientCollection, setMaximumClientCollection] = useState<string>('')
    const [facility, setFacility] = useState<string>('PCP(SE700)')
    const [isFacilityEditable, setIsFacilityEditable] = useState<boolean>(false)
    const [errors, setErrors] = useState<{ [key: string]: string }>({})
    const [introducers, setIntroducers] = useState<IntroducerOption[]>([])
    const [introducersLoading, setIntroducersLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [results, setResults] = useState<any[]>([])
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)
    const [progress, setProgress] = useState<{
        percent: number
        message: string
        stage: string
    } | null>(null)
    const [progressLog, setProgressLog] = useState<
        Array<{
            type: string
            stage: string
            percent: number
            message: string
            data?: any
            timestamp: Date
        }>
    >([])
    const [metrics, setMetrics] = useState<any>(null)
    const [summary, setSummary] = useState<any>(null)
    const [batchGroup, setBatchGroup] = useState<string | null>(null)
    const [isProgressLogExpanded, setIsProgressLogExpanded] = useState<boolean>(false)
    const [expandedReason, setExpandedReason] = useState<string | null>(null)
    const batchGroupShownRef = useRef<boolean>(false)
    const { toast } = useToast()

    // Default calculation date: today + 1 year + 2 months (matches the
    // "1 Year + 2 Months" preset on the main collect form).
    const defaultCsvCalcDate = (() => {
        const d = new Date()
        d.setFullYear(d.getFullYear() + 1)
        d.setMonth(d.getMonth() + 2)
        return d.toISOString().split('T')[0]
    })()

    // CSV "re-add fixed clients" section state
    const [csvOpen, setCsvOpen] = useState<boolean>(false)
    const [csvFile, setCsvFile] = useState<File | null>(null)
    const [csvFileBase64, setCsvFileBase64] = useState<string | null>(null)
    const [csvNotes, setCsvNotes] = useState<string>('')
    const [csvCommission, setCsvCommission] = useState<string>('10')
    const [csvStatInterest, setCsvStatInterest] = useState<string>('8')
    const [csvFacility, setCsvFacility] = useState<string>('PCP(SE700)')
    const [csvCalculationDate, setCsvCalculationDate] = useState<string>(defaultCsvCalcDate)
    const [csvSubmitting, setCsvSubmitting] = useState<boolean>(false)
    const [csvProgress, setCsvProgress] = useState<{ percent: number; message: string; stage: string } | null>(null)
    const [csvProgressLog, setCsvProgressLog] = useState<CsvProgressEntry[]>([])
    const [csvProgressLogExpanded, setCsvProgressLogExpanded] = useState<boolean>(false)
    const [csvResult, setCsvResult] = useState<CsvResult | null>(null)
    const [csvError, setCsvError] = useState<string | null>(null)
    const csvFileInputRef = useRef<HTMLInputElement>(null)

    const datePresets = [
        { label: 'Today', value: 'today' },
        { label: '1 Year + 2 Months', value: '1year2months' },
        { label: 'Custom Date', value: 'custom' },
    ]

    // Fetch introducers on component mount
    useEffect(() => {
        const fetchIntroducers = async () => {
            try {
                setIntroducersLoading(true)
                const response = await api({ url: '/introducers-list', data: {} })

                if (response.data && response.data.success) {
                    setIntroducers(response.data.introducers || [])
                } else {
                    // Fallback to static data if API fails
                    setIntroducers([
                        { value: 'ADL', label: 'All Day Loans' },
                        { value: 'MR', label: 'CheckFinance' },
                        { value: 'SNT', label: 'Sentinel' },
                        { value: 'PCPMO26', label: 'PCPMO26' },
                        { value: 'MBFCheck', label: 'MBF Check' },
                        { value: 'FunderAdditional', label: 'Funder Additional' },
                        { value: 'FundingExtended', label: 'Funding Extended' },
                        { value: 'boshhImport', label: 'Boshh Import' },
                        { value: 'sid', label: 'SID (System Import)' },
                    ])
                }
            } catch (error) {
                console.error('Error fetching introducers:', error)
                // Fallback to static data if API fails
                setIntroducers([
                    { value: 'ADL', label: 'All Day Loans' },
                    { value: 'MR', label: 'CheckFinance' },
                    { value: 'SNT', label: 'Sentinel' },
                    { value: 'PCPMO26', label: 'PCPMO26' },
                    { value: 'MBFCheck', label: 'MBF Check' },
                    { value: 'FunderAdditional', label: 'Funder Additional' },
                    { value: 'FundingExtended', label: 'Funding Extended' },
                    { value: 'boshhImport', label: 'Boshh Import' },
                    { value: 'sid', label: 'SID (System Import)' },
                ])
            } finally {
                setIntroducersLoading(false)
            }
        }

        fetchIntroducers()
    }, [])

    const copyToClipboard = useCallback(
        async (text: string) => {
            try {
                await navigator.clipboard.writeText(text)
                toast({
                    title: 'Copied!',
                    description: 'Batch ID copied to clipboard',
                    duration: 2000,
                })
            } catch (err) {
                console.error('Failed to copy:', err)
                toast({
                    title: 'Failed to copy',
                    description: 'Could not copy batch ID to clipboard',
                    duration: 3000,
                })
            }
        },
        [toast]
    )

    const showBatchGroupToast = useCallback(
        (batchId: string) => {
            toast({
                title: 'Batch Group Created',
                description: batchId,
                action: {
                    label: 'Copy',
                    onClick: () => copyToClipboard(batchId),
                },
                duration: 10000,
            })
        },
        [toast, copyToClipboard]
    )

    // Show toast notification when batch group is set
    useEffect(() => {
        if (batchGroup && !batchGroupShownRef.current) {
            batchGroupShownRef.current = true
            showBatchGroupToast(batchGroup)
        }
    }, [batchGroup, showBatchGroupToast])

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {}

        if (commissionPercentage && (parseFloat(commissionPercentage) < 0 || parseFloat(commissionPercentage) > 100)) {
            newErrors.commission = 'Commission must be between 0 and 100'
        }

        if (
            statutoryInterestPercentage &&
            (parseFloat(statutoryInterestPercentage) < 0 || parseFloat(statutoryInterestPercentage) > 100)
        ) {
            newErrors.statutory = 'Statutory interest must be between 0 and 100'
        }

        if (minimumClientValue && parseFloat(minimumClientValue) < 0) {
            newErrors.minimumClientValue = 'Minimum client value cannot be negative'
        }

        if (maximumClientCollection && parseFloat(maximumClientCollection) < 0) {
            newErrors.maximumClientCollection = 'Maximum client collection cannot be negative'
        }

        if (calculationDate === 'custom' && !customDate) {
            newErrors.customDate = 'Please select a custom date'
        }

        if (selectedIntroducers.length === 0) {
            newErrors.introducers = 'Select at least one introducer'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const getDateValue = () => {
        if (calculationDate === 'today') {
            return new Date().toISOString()
        } else if (calculationDate === '1year2months') {
            const date = new Date()
            date.setFullYear(date.getFullYear() + 1)
            date.setMonth(date.getMonth() + 2)
            return date.toISOString()
        } else if (calculationDate === 'custom') {
            return new Date(customDate).toISOString()
        } else if (calculationDate) {
            return new Date(calculationDate).toISOString()
        }
        return undefined
    }

    // Transform allocation records to table format
    const transformAllocationRecords = (allocationRecords: any[]) => {
        return allocationRecords.map((record) => {
            const clientData = record.funderClientDataRecord || {}
            const clientName = [clientData.first_name, clientData.last_name].filter(Boolean).join(' ') || 'N/A'

            // Extract introducer from clientKey (format: "INTRODUCER_clientId")
            const introducer = record.clientKey?.split('_')[0] || clientData.introducer || 'N/A'

            return {
                clientName,
                email: clientData.email_address || 'N/A',
                introducer,
                commissionPercentage: commissionPercentage || 'N/A',
                statutoryInterestPercentage: statutoryInterestPercentage || 'N/A',
                clientValue: record.clientTotalValue || 0,
                collectionAmount: record.clientTotalValue || 0,
                // Keep original data for reference
                originalData: record,
            }
        })
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!validateForm()) {
            Report.failure('Validation Error', 'Please correct the errors in the form', 'OKAY')
            return
        }

        setIsSubmitting(true)
        setResults([])
        setHasSubmitted(true)
        setProgress({ percent: 0, message: 'Starting...', stage: 'init' })
        setProgressLog([])
        setMetrics(null)
        setSummary(null)

        try {
            const storedAccessToken = window.localStorage.getItem('access')
            if (!storedAccessToken) {
                window.location.href = getLoginUrl()
                return
            }

            const apiUrl = `${process.env.REACT_APP_API_URL}/collect-data-for-funding`
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: storedAccessToken,
                },
                body: JSON.stringify({
                    commissionPercentage: commissionPercentage ? parseFloat(commissionPercentage) : undefined,
                    statInterestPercentage: statutoryInterestPercentage
                        ? parseFloat(statutoryInterestPercentage)
                        : undefined,
                    minimumClientValue: minimumClientValue ? parseFloat(minimumClientValue) : undefined,
                    calculationDate: getDateValue(),
                    introducer: selectedIntroducers,
                    maximumClientCollection: maximumClientCollection ? parseFloat(maximumClientCollection) : undefined,
                    facility: facility,
                    format: 'stream',
                }),
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            if (!response.body) {
                throw new Error('Response body is null')
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ''
            let finalResult: any = null

            const handleUpdate = (update: any) => {
                const logEntry = {
                    type: update.type || 'info',
                    stage: update.stage || '',
                    percent: update.percent || 0,
                    message: update.message || '',
                    data: update.data,
                    timestamp: new Date(),
                }

                setProgressLog((prev) => [...prev, logEntry])

                if (update.type === 'progress' || update.type === 'info') {
                    setProgress({
                        percent: update.percent || 0,
                        message: update.message || '',
                        stage: update.stage || '',
                    })
                }

                if (update.type === 'error') {
                    setProgress({
                        percent: update.percent || 0,
                        message: update.message || 'Error occurred',
                        stage: 'error',
                    })
                    Report.failure('Error', update.message || 'An error occurred', 'OKAY')
                }

                if (update.type === 'complete') {
                    finalResult = update.data
                    setProgress({
                        percent: 100,
                        message: update.message || 'Complete',
                        stage: 'done',
                    })

                    if (finalResult?.batchGroup) {
                        setBatchGroup(finalResult.batchGroup)
                    } else if (finalResult?.allocationGroup) {
                        setBatchGroup(finalResult.allocationGroup)
                    } else if (finalResult?.batch_id) {
                        setBatchGroup(finalResult.batch_id)
                    } else if (finalResult?.data?.batchGroup) {
                        setBatchGroup(finalResult.data.batchGroup)
                    } else if (finalResult?.data?.allocationGroup) {
                        setBatchGroup(finalResult.data.allocationGroup)
                    }

                    if (finalResult?.metrics) {
                        setMetrics(finalResult.metrics)
                        setExpandedReason(null)

                        const m = finalResult.metrics
                        console.group(
                            `[Funding batch ${finalResult.batchGroup || ''}] Rejection breakdown`
                        )
                        console.table({
                            'Clients - CCJ/VA': m.clientsWithCcjOrVA ?? 0,
                            'Clients - Missing CFA': m.clientsExcludedMissingCfa ?? 0,
                            'Clients - Below min value': m.clientsBelowMinimum ?? 0,
                            'Clients - Duplicate email': m.clientsWithDuplicateEmail ?? 0,
                            'Claims - Below £5k loan': m.claimsExcludedBelowMinLoan ?? 0,
                            'Claims - Duplicate policy no.': m.claimsExcludedDuplicatePolicyNumber ?? 0,
                            'Claims - Unapproved lender': m.claimsExcludedUnapprovedLender ?? 0,
                            'Claims - No length of contract': m.claimsExcludedNoLengthOfContract ?? 0,
                            'Claims - Contract < 5 months': m.claimsExcludedBelowMinContractLength ?? 0,
                            'Claims - Repayment > £1,500': m.claimsExcludedExcessiveMonthlyRepayment ?? 0,
                            'Claims - Repayment < £50': m.claimsExcludedBelowMinMonthlyRepayment ?? 0,
                            'Claims - Pre-2007 start date': m.claimsExcludedPre2007 ?? 0,
                        })
                        console.groupEnd()
                    }
                    if (finalResult?.summary) {
                        setSummary(finalResult.summary)
                    }

                    if (finalResult?.allocationRecords && Array.isArray(finalResult.allocationRecords)) {
                        const transformed = transformAllocationRecords(finalResult.allocationRecords)
                        setResults(transformed)
                    } else if (
                        finalResult?.data?.funderClientDataRecords &&
                        Array.isArray(finalResult.data.funderClientDataRecords)
                    ) {
                        const transformed = finalResult.data.funderClientDataRecords.map((record: any) => ({
                            clientName:
                                [record.first_name, record.last_name].filter(Boolean).join(' ') || 'N/A',
                            email: record.email_address || 'N/A',
                            introducer: record.introducer_client_id?.split('_')[0] || 'N/A',
                            commissionPercentage: commissionPercentage || 'N/A',
                            statutoryInterestPercentage: statutoryInterestPercentage || 'N/A',
                            clientValue: 0,
                            collectionAmount: 0,
                            originalData: record,
                        }))
                        setResults(transformed)
                    } else if (finalResult?.data && Array.isArray(finalResult.data)) {
                        setResults(finalResult.data)
                    } else if (finalResult?.results && Array.isArray(finalResult.results)) {
                        setResults(finalResult.results)
                    }
                }
            }

            while (true) {
                const { done, value } = await reader.read()

                if (done) {
                    buffer += decoder.decode()
                    if (buffer.trim()) {
                        try {
                            handleUpdate(JSON.parse(buffer))
                        } catch (parseError) {
                            console.error('Error parsing trailing JSON line:', parseError, 'Line:', buffer)
                        }
                        buffer = ''
                    }
                    break
                }

                buffer += decoder.decode(value, { stream: true })
                const lines = buffer.split('\n')
                buffer = lines.pop() || ''

                for (const line of lines) {
                    if (!line.trim()) continue
                    try {
                        handleUpdate(JSON.parse(line))
                    } catch (parseError) {
                        console.error('Error parsing JSON line:', parseError, 'Line:', line)
                    }
                }
            }
        } catch (error: any) {
            console.error('Submit error:', error)
            Report.failure('Submit Error', error.message || 'Failed to submit data', 'OKAY')
            setHasSubmitted(false)
            setProgress(null)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCsvFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setCsvFile(file)
        setCsvResult(null)
        setCsvError(null)

        const reader = new FileReader()
        reader.onload = (event) => {
            const base64 = (event.target?.result as string).split(',')[1]
            setCsvFileBase64(base64 || null)
        }
        reader.readAsDataURL(file)
    }, [])

    const handleCsvSubmit = useCallback(async () => {
        if (!csvFileBase64) {
            setCsvError('Please choose a CSV file first.')
            return
        }
        setCsvSubmitting(true)
        setCsvError(null)
        setCsvResult(null)
        setCsvProgress({ percent: 0, message: 'Starting...', stage: 'init' })
        setCsvProgressLog([])

        try {
            const storedAccessToken = window.localStorage.getItem('access')
            if (!storedAccessToken) {
                window.location.href = getLoginUrl()
                return
            }

            const apiUrl = `${process.env.REACT_APP_API_URL}/collect-clients-by-email-csv`
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: storedAccessToken,
                },
                body: JSON.stringify({
                    fileData: csvFileBase64,
                    notes: csvNotes,
                    commissionPercentage: csvCommission ? parseFloat(csvCommission) : undefined,
                    statInterestPercentage: csvStatInterest ? parseFloat(csvStatInterest) : undefined,
                    calculationDate: csvCalculationDate ? new Date(csvCalculationDate).toISOString() : undefined,
                    facility: csvFacility || undefined,
                    introducers: CSV_DEFAULT_INTRODUCERS,
                    format: 'stream',
                }),
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            if (!response.body) {
                throw new Error('Response body is null')
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ''
            let finalData: any = null

            while (true) {
                const { done, value } = await reader.read()
                if (done) break

                buffer += decoder.decode(value, { stream: true })
                const lines = buffer.split('\n')
                buffer = lines.pop() || ''

                for (const line of lines) {
                    if (!line.trim()) continue
                    try {
                        const update = JSON.parse(line)
                        setCsvProgressLog((prev) => [
                            ...prev,
                            {
                                type: update.type || 'info',
                                stage: update.stage || '',
                                percent: update.percent || 0,
                                message: update.message || '',
                                data: update.data,
                                timestamp: new Date(),
                            },
                        ])

                        if (update.type === 'progress' || update.type === 'info') {
                            setCsvProgress({
                                percent: update.percent || 0,
                                message: update.message || '',
                                stage: update.stage || '',
                            })
                        }

                        if (update.type === 'error') {
                            setCsvProgress({
                                percent: update.percent || 0,
                                message: update.message || 'Error occurred',
                                stage: 'error',
                            })
                            setCsvError(update.message || 'Error occurred')
                        }

                        if (update.type === 'complete') {
                            finalData = update.data
                            setCsvProgress({
                                percent: 100,
                                message: update.message || 'Complete',
                                stage: 'done',
                            })
                        }
                    } catch (parseErr) {
                        console.error('Error parsing CSV NDJSON line:', parseErr, 'Line:', line)
                    }
                }
            }

            if (finalData) {
                setCsvResult(finalData as CsvResult)
                setCsvFile(null)
                setCsvFileBase64(null)
                if (csvFileInputRef.current) csvFileInputRef.current.value = ''
            }
        } catch (err: any) {
            console.error('CSV submit error:', err)
            setCsvError(err?.message || 'Failed to add clients from CSV')
        } finally {
            setCsvSubmitting(false)
        }
    }, [csvFileBase64, csvNotes, csvCommission, csvStatInterest, csvCalculationDate, csvFacility])

    const handleClear = () => {
        setCommissionPercentage('')
        setStatutoryInterestPercentage('')
        setMinimumClientValue('')
        setCalculationDate('')
        setCustomDate('')
        setSelectedIntroducers([])
        setMaximumClientCollection('')
        setFacility('PCP(SE700)')
        setIsFacilityEditable(false)
        setResults([])
        setHasSubmitted(false)
        setProgress(null)
        setProgressLog([])
        setMetrics(null)
        setSummary(null)
        setBatchGroup(null)
        setErrors({})
    }

    return (
        <div className={styles.collectFundingContainer}>
            <div className={`${styles.formCard} ${styles.csvReaddCard}`}>
                <div
                    className={styles.csvReaddHeader}
                    onClick={() => setCsvOpen((v) => !v)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setCsvOpen((v) => !v)
                        }
                    }}
                >
                    <div className={styles.csvReaddTitleGroup}>
                        <h3 className={styles.csvReaddTitle}>
                            <FaFileCsv style={{ marginRight: 8, color: '#1565c0' }} />
                            <span>Re-add fixed clients from CSV</span>
                        </h3>
                    </div>
                    <FaChevronDown className={`${styles.csvChevron} ${csvOpen ? styles.csvChevronOpen : ''}`} />
                </div>

                {csvOpen && (
                    <div className={styles.csvBody}>
                        <p className={styles.csvReaddSubtitle}>
                            Upload a CSV of email addresses. Each email is looked up across the introducer databases
                            (Funder Additional, Funding Extended, PCPMO26, Check Finance, LC Leads, PCP Sentinel). Matching clients are
                            run through the standard client valuation (approved lender + value ≥ £1,700) and placed
                            in a new funding batch. Emails that already exist in the funding monitor schema are
                            skipped automatically.
                        </p>

                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label>Email CSV file <span className={styles.required}>*</span></label>
                                <label className={styles.csvFileLabel}>
                                    <FaUpload style={{ marginRight: 6 }} />
                                    {csvFile ? csvFile.name : 'Select .csv file'}
                                    <input
                                        type="file"
                                        accept=".csv,text/csv"
                                        onChange={handleCsvFileChange}
                                        ref={csvFileInputRef}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="csvCalcDate">Calculation Date</label>
                                <input
                                    id="csvCalcDate"
                                    type="date"
                                    value={csvCalculationDate}
                                    onChange={(e) => setCsvCalculationDate(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label htmlFor="csvCommission">Commission Percentage</label>
                                <input
                                    id="csvCommission"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={csvCommission}
                                    onChange={(e) => setCsvCommission(e.target.value)}
                                    placeholder="e.g. 10"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="csvStatInterest">Statutory Interest Percentage</label>
                                <input
                                    id="csvStatInterest"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={csvStatInterest}
                                    onChange={(e) => setCsvStatInterest(e.target.value)}
                                    placeholder="e.g. 8"
                                />
                            </div>
                        </div>

                        <div className={styles.formRowFullWidth}>
                            <div className={styles.formGroup}>
                                <label htmlFor="csvFacility">Facility</label>
                                <input
                                    id="csvFacility"
                                    type="text"
                                    value={csvFacility}
                                    onChange={(e) => setCsvFacility(e.target.value)}
                                    placeholder="e.g. PCP(SE700)"
                                />
                            </div>
                        </div>

                        <div className={styles.formRowFullWidth}>
                            <div className={styles.formGroup}>
                                <label htmlFor="csvNotes">Batch notes</label>
                                <textarea
                                    id="csvNotes"
                                    className={styles.csvNotesField}
                                    rows={3}
                                    value={csvNotes}
                                    onChange={(e) => setCsvNotes(e.target.value)}
                                    placeholder="Describe this batch (e.g. why these clients were re-added)"
                                />
                            </div>
                        </div>

                        {csvError && <p className={styles.csvErrorText}>{csvError}</p>}

                        <div className={styles.submitWrapper}>
                            <button
                                type="button"
                                className={styles.submitButton}
                                onClick={handleCsvSubmit}
                                disabled={csvSubmitting || !csvFileBase64}
                            >
                                {csvSubmitting ? 'Processing...' : 'Add clients to new batch'}
                            </button>
                        </div>

                        {csvProgress && (
                            <div className={styles.progressSection} style={{ marginTop: 20 }}>
                                <div className={styles.progressHeader}>
                                    <span className={styles.progressStage}>{csvProgress.stage}</span>
                                    <span className={styles.progressPercent}>{csvProgress.percent}%</span>
                                </div>
                                <div className={styles.progressBar}>
                                    <div
                                        className={styles.progressBarFill}
                                        style={{ width: `${csvProgress.percent}%` }}
                                    />
                                </div>
                                <p className={styles.progressMessage}>{csvProgress.message}</p>
                            </div>
                        )}

                        {csvProgressLog.length > 0 && (
                            <div className={styles.progressLogSection}>
                                <div
                                    className={styles.progressLogHeader}
                                    onClick={() => setCsvProgressLogExpanded((v) => !v)}
                                >
                                    <h4 className={styles.progressLogTitle}>
                                        Progress Log
                                        <span className={styles.logCount}>({csvProgressLog.length} entries)</span>
                                    </h4>
                                    <button className={styles.expandButton} type="button">
                                        {csvProgressLogExpanded ? 'Collapse' : 'Expand'}
                                        <span
                                            className={`${styles.expandIcon} ${
                                                csvProgressLogExpanded ? styles.expanded : ''
                                            }`}
                                        >
                                            ▼
                                        </span>
                                    </button>
                                </div>
                                {csvProgressLogExpanded && (
                                    <div className={styles.progressLogContainer}>
                                        {csvProgressLog.map((log, idx) => (
                                            <div
                                                key={idx}
                                                className={`${styles.progressLogEntry} ${styles[`logType${log.type}`]}`}
                                            >
                                                <div className={styles.logHeader}>
                                                    <span className={styles.logType}>{log.type}</span>
                                                    <span className={styles.logStage}>{log.stage}</span>
                                                    {log.percent > 0 && (
                                                        <span className={styles.logPercent}>{log.percent}%</span>
                                                    )}
                                                    <span className={styles.logTime}>
                                                        {log.timestamp.toLocaleTimeString()}
                                                    </span>
                                                </div>
                                                <p className={styles.logMessage}>{log.message}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {csvResult && (
                            <div className={styles.csvResultCard}>
                                <h4>Batch created: {csvResult.batchGroup}</h4>
                                {csvResult.notes && (
                                    <p>
                                        <strong>Notes:</strong> {csvResult.notes}
                                    </p>
                                )}
                                <div className={styles.csvResultGrid}>
                                    <div className={styles.csvResultItem}>
                                        <span className={styles.csvResultValue}>{csvResult.requestedEmails}</span>
                                        <span className={styles.csvResultLabel}>Emails in CSV</span>
                                    </div>
                                    <div className={styles.csvResultItem}>
                                        <span className={styles.csvResultValue}>{csvResult.matchedClients}</span>
                                        <span className={styles.csvResultLabel}>Clients added</span>
                                    </div>
                                    <div className={styles.csvResultItem}>
                                        <span className={styles.csvResultValue}>
                                            {csvResult.alreadyInFundingMonitorCount}
                                        </span>
                                        <span className={styles.csvResultLabel}>Already in funding monitor</span>
                                    </div>
                                    <div className={styles.csvResultItem}>
                                        <span className={styles.csvResultValue}>{csvResult.notFoundEmailsCount}</span>
                                        <span className={styles.csvResultLabel}>Not found / failed valuation</span>
                                    </div>
                                </div>

                                {csvResult.alreadyInFundingMonitorSample?.length ? (
                                    <p className={styles.csvResultDetail}>
                                        <strong>Already present (sample):</strong>{' '}
                                        {csvResult.alreadyInFundingMonitorSample.slice(0, 12).join(', ')}
                                        {csvResult.alreadyInFundingMonitorSample.length > 12 ? '…' : ''}
                                    </p>
                                ) : null}
                                {csvResult.notFoundEmailsSample?.length ? (
                                    <p className={styles.csvResultDetail}>
                                        <strong>Not added (sample):</strong>{' '}
                                        {csvResult.notFoundEmailsSample.slice(0, 12).join(', ')}
                                        {csvResult.notFoundEmailsSample.length > 12 ? '…' : ''}
                                    </p>
                                ) : null}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className={styles.formCard}>
                <div className={styles.formHeader}>
                    <h3>Collect Funding Clients</h3>
                    <p className={styles.description}>Use the fields below to collect data for funding.</p>
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.formRowFullWidth}>
                        <div className={styles.formGroup}>
                            <label htmlFor="facility">Facility</label>
                            <div className={styles.facilityInputWrapper}>
                                <input
                                    id="facility"
                                    type="text"
                                    value={facility}
                                    onChange={(e) => setFacility(e.target.value)}
                                    placeholder="e.g. PCP(SE700)"
                                    disabled={!isFacilityEditable}
                                    className={!isFacilityEditable ? styles.disabled : ''}
                                />
                                <button
                                    type="button"
                                    className={styles.editButton}
                                    onClick={() => setIsFacilityEditable(!isFacilityEditable)}
                                >
                                    {isFacilityEditable ? 'Lock' : 'Edit'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="commission">Commission Percentage</label>
                            <input
                                id="commission"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={commissionPercentage}
                                onChange={(e) => setCommissionPercentage(e.target.value)}
                                className={errors.commission ? styles.error : ''}
                                placeholder="e.g. 10"
                            />
                            {errors.commission && <span className={styles.errorText}>{errors.commission}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="statutory">Statutory Interest Percentage</label>
                            <input
                                id="statutory"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={statutoryInterestPercentage}
                                onChange={(e) => setStatutoryInterestPercentage(e.target.value)}
                                className={errors.statutory ? styles.error : ''}
                                placeholder="e.g. 8"
                            />
                            {errors.statutory && <span className={styles.errorText}>{errors.statutory}</span>}
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="minClientValue">Minimum Client Value</label>
                            <input
                                id="minClientValue"
                                type="number"
                                min="0"
                                step="0.01"
                                value={minimumClientValue}
                                onChange={(e) => setMinimumClientValue(e.target.value)}
                                className={errors.minimumClientValue ? styles.error : ''}
                                placeholder="e.g. 500"
                            />
                            {errors.minimumClientValue && (
                                <span className={styles.errorText}>{errors.minimumClientValue}</span>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="maxClientCollection">Maximum Client Collection</label>
                            <input
                                id="maxClientCollection"
                                type="number"
                                min="0"
                                step="0.01"
                                value={maximumClientCollection}
                                onChange={(e) => setMaximumClientCollection(e.target.value)}
                                className={errors.maximumClientCollection ? styles.error : ''}
                                placeholder="e.g. 10000"
                            />
                            {errors.maximumClientCollection && (
                                <span className={styles.errorText}>{errors.maximumClientCollection}</span>
                            )}
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="date">Calculation Date</label>
                            <div className={styles.datePresets}>
                                {datePresets.map((preset) => (
                                    <button
                                        key={preset.value}
                                        type="button"
                                        className={`${styles.presetButton} ${
                                            calculationDate === preset.value ? styles.active : ''
                                        }`}
                                        onClick={() => setCalculationDate(preset.value)}
                                    >
                                        {preset.label}
                                    </button>
                                ))}
                            </div>
                            {errors.date && <span className={styles.errorText}>{errors.date}</span>}

                            {calculationDate === 'custom' && (
                                <div className={styles.customDateWrapper}>
                                    <input
                                        type="date"
                                        value={customDate}
                                        onChange={(e) => setCustomDate(e.target.value)}
                                        className={errors.customDate ? styles.error : ''}
                                    />
                                    {errors.customDate && <span className={styles.errorText}>{errors.customDate}</span>}
                                </div>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <div className={styles.introducerHeader}>
                                <label>Introducers</label>
                                <div className={styles.introducerActions}>
                                    <button
                                        type="button"
                                        className={styles.introducerActionButton}
                                        onClick={() =>
                                            setSelectedIntroducers(introducers.map((i) => i.value))
                                        }
                                        disabled={introducersLoading || introducers.length === 0}
                                    >
                                        Select all
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.introducerActionButton}
                                        onClick={() => setSelectedIntroducers([])}
                                        disabled={introducersLoading || selectedIntroducers.length === 0}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                            <div
                                className={`${styles.introducerList} ${
                                    errors.introducers ? styles.error : ''
                                }`}
                            >
                                {introducersLoading ? (
                                    <div className={styles.introducerLoading}>Loading introducers...</div>
                                ) : introducers.length === 0 ? (
                                    <div className={styles.introducerLoading}>No introducers available</div>
                                ) : (
                                    introducers.map((option) => {
                                        const checked = selectedIntroducers.includes(option.value)
                                        return (
                                            <label
                                                key={option.value}
                                                className={`${styles.introducerOption} ${
                                                    checked ? styles.introducerOptionChecked : ''
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={(e) => {
                                                        setSelectedIntroducers((prev) =>
                                                            e.target.checked
                                                                ? [...prev, option.value]
                                                                : prev.filter((v) => v !== option.value)
                                                        )
                                                    }}
                                                />
                                                <span>{option.label}</span>
                                            </label>
                                        )
                                    })
                                )}
                            </div>
                            {errors.introducers && (
                                <span className={styles.errorText}>{errors.introducers}</span>
                            )}
                        </div>
                    </div>

                    <div className={styles.submitWrapper}>
                        <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                        {hasSubmitted && (
                            <button type="button" className={styles.clearButton} onClick={handleClear}>
                                Clear
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* Results Section */}
            {hasSubmitted && (
                <div className={styles.resultsContainer}>
                    <div className={styles.resultsHeader}>
                        <h3>Results</h3>
                        <span className={styles.resultCount}>
                            {results.length > 0 && (
                                <>
                                    {results.length} {results.length === 1 ? 'client' : 'clients'}
                                </>
                            )}
                        </span>
                    </div>

                    {/* Progress Section */}
                    {progress && (
                        <div className={styles.progressSection}>
                            <div className={styles.progressHeader}>
                                <span className={styles.progressStage}>{progress.stage}</span>
                                <span className={styles.progressPercent}>{progress.percent}%</span>
                            </div>
                            <div className={styles.progressBar}>
                                <div className={styles.progressBarFill} style={{ width: `${progress.percent}%` }} />
                            </div>
                            <p className={styles.progressMessage}>{progress.message}</p>
                        </div>
                    )}

                    {/* Progress Log */}
                    {progressLog.length > 0 && (
                        <div className={styles.progressLogSection}>
                            <div
                                className={styles.progressLogHeader}
                                onClick={() => setIsProgressLogExpanded(!isProgressLogExpanded)}
                            >
                                <h4 className={styles.progressLogTitle}>
                                    Progress Log
                                    <span className={styles.logCount}>({progressLog.length} entries)</span>
                                </h4>
                                <button className={styles.expandButton} type="button">
                                    {isProgressLogExpanded ? 'Collapse' : 'Expand'}
                                    <span
                                        className={`${styles.expandIcon} ${
                                            isProgressLogExpanded ? styles.expanded : ''
                                        }`}
                                    >
                                        ▼
                                    </span>
                                </button>
                            </div>
                            {isProgressLogExpanded && (
                                <div className={styles.progressLogContainer}>
                                    {progressLog.map((log, index) => (
                                        <div
                                            key={index}
                                            className={`${styles.progressLogEntry} ${styles[`logType${log.type}`]}`}
                                        >
                                            <div className={styles.logHeader}>
                                                <span className={styles.logType}>{log.type}</span>
                                                <span className={styles.logStage}>{log.stage}</span>
                                                {log.percent > 0 && (
                                                    <span className={styles.logPercent}>{log.percent}%</span>
                                                )}
                                                <span className={styles.logTime}>
                                                    {log.timestamp.toLocaleTimeString()}
                                                </span>
                                            </div>
                                            <p className={styles.logMessage}>{log.message}</p>
                                            {log.data && (
                                                <div className={styles.logData}>
                                                    {log.data.recordType && (
                                                        <span className={styles.logDataItem}>
                                                            {log.data.recordType}: {log.data.current}/{log.data.total}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {!isProgressLogExpanded && progressLog.length > 0 && (
                                <div className={styles.progressLogPreview}>
                                    <div className={styles.previewEntry}>
                                        <span className={styles.previewType}>
                                            {progressLog[progressLog.length - 1].type}
                                        </span>
                                        <span className={styles.previewMessage}>
                                            {progressLog[progressLog.length - 1].message}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Metrics Section */}
                    {metrics && (
                        <div className={styles.metricsSection}>
                            <h4 className={styles.metricsTitle}>Metrics</h4>

                            <h5 className={styles.metricsSubtitle}>Totals</h5>
                            <div className={styles.metricsGrid}>
                                <div className={styles.metricItem}>
                                    <span className={styles.metricLabel}>Total Clients Processed</span>
                                    <span className={styles.metricValue}>
                                        {metrics.totalClientsProcessed?.toLocaleString() || 0}
                                    </span>
                                </div>
                                <div className={styles.metricItem}>
                                    <span className={styles.metricLabel}>Eligible Clients</span>
                                    <span className={styles.metricValue}>
                                        {metrics.eligibleClients?.toLocaleString() || 0}
                                    </span>
                                </div>
                                <div className={styles.metricItem}>
                                    <span className={styles.metricLabel}>Ineligible Clients</span>
                                    <span className={styles.metricValue}>
                                        {metrics.ineligibleClients?.toLocaleString() || 0}
                                    </span>
                                </div>
                                <div className={styles.metricItem}>
                                    <span className={styles.metricLabel}>Total Claims Processed</span>
                                    <span className={styles.metricValue}>
                                        {metrics.totalClaimsProcessed?.toLocaleString() || 0}
                                    </span>
                                </div>
                                <div className={styles.metricItem}>
                                    <span className={styles.metricLabel}>Total Claims Excluded</span>
                                    <span className={styles.metricValue}>
                                        {metrics.totalClaimsExcluded?.toLocaleString() || 0}
                                    </span>
                                </div>
                                <div className={styles.metricItem}>
                                    <span className={styles.metricLabel}>Total Book Value</span>
                                    <span className={styles.metricValue}>
                                        £
                                        {metrics.totalBookValue?.toLocaleString('en-GB', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }) || '0.00'}
                                    </span>
                                </div>
                            </div>

                            {(() => {
                                type ColumnDef = {
                                    field: string
                                    label: string
                                    format?: (v: any) => string
                                }
                                type ReasonDef = {
                                    key: string
                                    label: string
                                    columns: ColumnDef[]
                                }

                                const fmtMoney = (v: any) =>
                                    typeof v === 'number'
                                        ? `£${v.toLocaleString('en-GB', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                          })}`
                                        : v ?? ''

                                const baseClaimColumns: ColumnDef[] = [
                                    { field: 'introducer', label: 'Introducer' },
                                    { field: 'case_ref', label: 'Case Ref' },
                                    { field: 'client_name', label: 'Client' },
                                    { field: 'email', label: 'Email' },
                                ]
                                const baseClientColumns: ColumnDef[] = [
                                    { field: 'introducer', label: 'Introducer' },
                                    { field: 'client_name', label: 'Client' },
                                    { field: 'email', label: 'Email' },
                                ]

                                const clientReasons: ReasonDef[] = [
                                    {
                                        key: 'clientsWithCcjOrVA',
                                        label: 'CCJ or Voluntary Arrangement',
                                        columns: [
                                            ...baseClientColumns,
                                            { field: 'ccjs', label: 'CCJs' },
                                            { field: 'voluntary_arrangements', label: 'VAs' },
                                        ],
                                    },
                                    {
                                        key: 'clientsExcludedMissingCfa',
                                        label: 'Missing CFA / signature date',
                                        columns: [
                                            ...baseClientColumns,
                                            { field: 'introducer_client_id', label: 'Introducer Client ID' },
                                        ],
                                    },
                                    {
                                        key: 'clientsBelowMinimum',
                                        label: 'Below minimum client value',
                                        columns: [
                                            ...baseClientColumns,
                                            { field: 'client_total_value', label: 'Client Value', format: fmtMoney },
                                            { field: 'minimum_required', label: 'Min Required', format: fmtMoney },
                                            { field: 'claim_count', label: 'Claims' },
                                        ],
                                    },
                                    {
                                        key: 'clientsWithDuplicateEmail',
                                        label: 'Duplicate email in funder DB',
                                        columns: [
                                            ...baseClientColumns,
                                            { field: 'client_total_value', label: 'Client Value', format: fmtMoney },
                                        ],
                                    },
                                ]

                                const claimReasons: ReasonDef[] = [
                                    {
                                        key: 'claimsExcludedBelowMinLoan',
                                        label: 'Below minimum loan value (£5,000)',
                                        columns: [
                                            ...baseClaimColumns,
                                            { field: 'lender', label: 'Lender' },
                                            { field: 'loan_value', label: 'Loan Value', format: fmtMoney },
                                            { field: 'minimum_required', label: 'Min Required', format: fmtMoney },
                                        ],
                                    },
                                    {
                                        key: 'claimsExcludedDuplicatePolicyNumber',
                                        label: 'Duplicate policy number',
                                        columns: [
                                            ...baseClaimColumns,
                                            { field: 'policy_number', label: 'Policy Number' },
                                        ],
                                    },
                                    {
                                        key: 'claimsExcludedUnapprovedLender',
                                        label: 'Unapproved / missing lender',
                                        columns: [...baseClaimColumns, { field: 'lender', label: 'Lender' }],
                                    },
                                    {
                                        key: 'claimsExcludedNoLengthOfContract',
                                        label: 'Missing or zero length of contract',
                                        columns: [
                                            ...baseClaimColumns,
                                            { field: 'lender', label: 'Lender' },
                                            { field: 'length_of_contract', label: 'Length' },
                                        ],
                                    },
                                    {
                                        key: 'claimsExcludedBelowMinContractLength',
                                        label: 'Contract length under 5 months',
                                        columns: [
                                            ...baseClaimColumns,
                                            { field: 'lender', label: 'Lender' },
                                            { field: 'length_of_contract', label: 'Length' },
                                            { field: 'minimum_required', label: 'Min Required' },
                                        ],
                                    },
                                    {
                                        key: 'claimsExcludedExcessiveMonthlyRepayment',
                                        label: 'Monthly repayment > £1,500',
                                        columns: [
                                            ...baseClaimColumns,
                                            { field: 'lender', label: 'Lender' },
                                            {
                                                field: 'monthly_repayment',
                                                label: 'Monthly Repayment',
                                                format: fmtMoney,
                                            },
                                            { field: 'maximum_allowed', label: 'Max Allowed', format: fmtMoney },
                                        ],
                                    },
                                    {
                                        key: 'claimsExcludedBelowMinMonthlyRepayment',
                                        label: 'Monthly repayment < £50',
                                        columns: [
                                            ...baseClaimColumns,
                                            { field: 'lender', label: 'Lender' },
                                            {
                                                field: 'monthly_repayment',
                                                label: 'Monthly Repayment',
                                                format: fmtMoney,
                                            },
                                            { field: 'minimum_required', label: 'Min Required', format: fmtMoney },
                                        ],
                                    },
                                    {
                                        key: 'claimsExcludedPre2007',
                                        label: 'Pre-2007 start date',
                                        columns: [...baseClaimColumns, { field: 'start_date', label: 'Start Date' }],
                                    },
                                ]

                                const allReasons = [...clientReasons, ...claimReasons]
                                const reasonByKey: Record<string, ReasonDef> = Object.fromEntries(
                                    allReasons.map((r) => [r.key, r])
                                )

                                const renderTile = (reason: ReasonDef) => {
                                    const count = metrics[reason.key] ?? 0
                                    const isOpen = expandedReason === reason.key
                                    const hasSamples = (metrics.samples?.[reason.key]?.length ?? 0) > 0
                                    return (
                                        <button
                                            key={reason.key}
                                            type="button"
                                            className={`${styles.metricItem} ${styles.metricItemClickable} ${
                                                isOpen ? styles.metricItemActive : ''
                                            } ${!hasSamples ? styles.metricItemDisabled : ''}`}
                                            onClick={() =>
                                                hasSamples &&
                                                setExpandedReason(isOpen ? null : reason.key)
                                            }
                                            disabled={!hasSamples}
                                            aria-expanded={isOpen}
                                            aria-controls={`drilldown-${reason.key}`}
                                        >
                                            <span className={styles.metricLabel}>{reason.label}</span>
                                            <span className={styles.metricValue}>
                                                {count?.toLocaleString?.() ?? count}
                                            </span>
                                            {hasSamples && (
                                                <span className={styles.metricHint}>
                                                    {isOpen ? 'Hide examples' : 'Click for examples'}
                                                </span>
                                            )}
                                        </button>
                                    )
                                }

                                const renderDrilldown = (groupKeys: string[]) => {
                                    if (!expandedReason || !groupKeys.includes(expandedReason)) return null
                                    const reason = reasonByKey[expandedReason]
                                    const samples: any[] = metrics.samples?.[expandedReason] ?? []
                                    const total = metrics[expandedReason] ?? 0
                                    return (
                                        <div
                                            id={`drilldown-${reason.key}`}
                                            className={styles.drilldownPanel}
                                        >
                                            <div className={styles.drilldownHeader}>
                                                <strong>{reason.label}</strong>
                                                <span className={styles.drilldownCount}>
                                                    Showing {samples.length} of {total.toLocaleString()}
                                                    {total > samples.length ? ' (first 10)' : ''}
                                                </span>
                                                <button
                                                    type="button"
                                                    className={styles.drilldownClose}
                                                    onClick={() => setExpandedReason(null)}
                                                    aria-label="Close drilldown"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            {samples.length === 0 ? (
                                                <p className={styles.drilldownEmpty}>No examples captured.</p>
                                            ) : (
                                                <div className={styles.drilldownTableWrapper}>
                                                    <table className={styles.drilldownTable}>
                                                        <thead>
                                                            <tr>
                                                                {reason.columns.map((c) => (
                                                                    <th key={c.field}>{c.label}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {samples.map((row, idx) => (
                                                                <tr key={idx}>
                                                                    {reason.columns.map((c) => {
                                                                        const raw = row[c.field]
                                                                        const display = c.format
                                                                            ? c.format(raw)
                                                                            : raw ?? ''
                                                                        return (
                                                                            <td key={c.field}>
                                                                                {display === '' ? '—' : String(display)}
                                                                            </td>
                                                                        )
                                                                    })}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )
                                }

                                return (
                                    <>
                                        <h5 className={styles.metricsSubtitle}>Clients excluded by reason</h5>
                                        <div className={styles.metricsGrid}>{clientReasons.map(renderTile)}</div>
                                        {renderDrilldown(clientReasons.map((r) => r.key))}

                                        <h5 className={styles.metricsSubtitle}>Claims excluded by reason</h5>
                                        <div className={styles.metricsGrid}>{claimReasons.map(renderTile)}</div>
                                        {renderDrilldown(claimReasons.map((r) => r.key))}
                                    </>
                                )
                            })()}
                        </div>
                    )}

                    {/* Summary Section */}
                    {summary && (
                        <div className={styles.summarySection}>
                            <h4 className={styles.summaryTitle}>Summary</h4>
                            <div className={styles.summaryGrid}>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>Total Eligible Clients</span>
                                    <span className={styles.summaryValue}>
                                        {summary.totalEligibleClients?.toLocaleString() || 0}
                                    </span>
                                </div>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>Total Ineligible Clients</span>
                                    <span className={styles.summaryValue}>
                                        {summary.totalIneligibleClients?.toLocaleString() || 0}
                                    </span>
                                </div>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>Total Book Value</span>
                                    <span className={styles.summaryValue}>
                                        £
                                        {summary.totalBookValue?.toLocaleString('en-GB', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }) || '0.00'}
                                    </span>
                                </div>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>Average Client Value</span>
                                    <span className={styles.summaryValue}>
                                        £
                                        {summary.averageClientValue?.toLocaleString('en-GB', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }) || '0.00'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {results.length > 0 ? (
                        <div className={styles.tableWrapper}>
                            <table className={styles.resultsTable}>
                                <thead>
                                    <tr>
                                        <th>Client Name</th>
                                        <th>Email</th>
                                        <th>Introducer</th>
                                        <th>Commission %</th>
                                        <th>Stat Interest %</th>
                                        <th>Client Value</th>
                                        <th>Collection Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((result, index) => (
                                        <tr key={index}>
                                            <td>{result.clientName || 'N/A'}</td>
                                            <td>{result.email || 'N/A'}</td>
                                            <td>{result.introducer || 'N/A'}</td>
                                            <td>
                                                {result.commissionPercentage !== 'N/A' &&
                                                result.commissionPercentage !== undefined
                                                    ? `${result.commissionPercentage}%`
                                                    : 'N/A'}
                                            </td>
                                            <td>
                                                {result.statutoryInterestPercentage !== 'N/A' &&
                                                result.statutoryInterestPercentage !== undefined
                                                    ? `${result.statutoryInterestPercentage}%`
                                                    : 'N/A'}
                                            </td>
                                            <td>
                                                {result.clientValue && result.clientValue > 0
                                                    ? `£${result.clientValue.toLocaleString('en-GB', {
                                                          minimumFractionDigits: 2,
                                                          maximumFractionDigits: 2,
                                                      })}`
                                                    : 'N/A'}
                                            </td>
                                            <td>
                                                {result.collectionAmount && result.collectionAmount > 0
                                                    ? `£${result.collectionAmount.toLocaleString('en-GB', {
                                                          minimumFractionDigits: 2,
                                                          maximumFractionDigits: 2,
                                                      })}`
                                                    : 'N/A'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className={styles.noResults}>
                            <p>No clients found matching your criteria.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
